import { useEffect, useRef, useState } from "react";
import AutoDashboard from "../Dashboard/AutoDashboard";
import ExportableChart from "../Charts/ExportableChart";
import { askGroq } from "../../features/ai/groqClient";
import { buildAnswerContext } from "../../features/ai/contextBuilder";
import { saveMessages, updateSession } from "../../firebase/firestore";
import { executePlan } from "../../features/analytics/executor/executePlan";
import { formatToolResult } from "../../features/ai/resultFormatter";
import { buildDatasetSchema } from "../../features/data/datasetSchema";
import { planQuery } from "../../features/analytics/planner/queryPlanner";
import { emptyContext, updateContext, describeContext } from "../../features/analytics/planner/conversationContext";
import { suggestFollowUpQuestions } from "../../features/ai/followUpSuggester";
import { generateSalesAnalysisReport, generateExecutiveSummary } from "../../features/report/reportGenerator";
import { exportConversation } from "../../features/export/chatExporter";
import { useSubscription } from "../../context/SubscriptionContext";
import { trackEvent } from "../../utils/analyticsEvents";
import ScheduleReportModal from "../Reports/ScheduleReportModal";
import Logo from "../common/Logo";

const PERSONA_RULES = `
You are InsightPulse AI, a conversational data analytics assistant.

You speak AS InsightPulse AI directly — never mention "analytics engine",
"tool", "planner", "pipeline", "local engine", or any other internal system
or architecture name. As far as the user is concerned, YOU analyzed the data
yourself. If you need to refer to how an answer was produced, just say
"I found that..." or "Looking at your data...".
`;

const ANSWER_FORMAT_RULES = `
Adapt your answer's structure to the complexity of the question — do not
force every answer into a rigid template:
- Simple questions (a single number, a single winner) deserve a short,
  direct answer, maybe one supporting sentence. Nothing more.
- Richer questions (breakdowns, trends, comparisons, "why") can include a
  brief interpretation of what the numbers mean for the business, after
  giving the direct answer first.
Do NOT list follow-up questions yourself — the interface already shows
clickable suggested questions separately, so repeating them creates
duplication.
If the computed result includes a note that it reflects observational
association rather than a confirmed cause, you MUST preserve that caution
in your own words — never say something "caused", "is due to", or "led to"
another thing. Say "associated with" or "the largest observed contributor".

CRITICAL: if the computed result below contains valid category/value data
(e.g. a breakdown listing real names and numbers), you MUST answer directly
from that data. NEVER say a breakdown "couldn't be determined" or that the
result "doesn't contain" some column's information if that column's values
are visibly listed in the computed result below — that would directly
contradict data that's right there. If the computed result is genuinely
empty or errored, only then say the question couldn't be answered.

THE ANALYTICS ENGINE IS AUTHORITATIVE ON AGGREGATION — these rules are
absolute and override any other instinct you have about phrasing:
1. The computed result below is authoritative. It already reflects every
   calculation needed to answer the question.
2. Trust the "Aggregation:" line in the computed result completely — it
   states EXACTLY which operation was performed on the numbers that follow.
3. If it says AVERAGE, every value that follows IS an average — present it
   as an average ("the average X is...", "on average...").
4. If it says SUM, every value that follows IS a total — present it as a
   total ("the total X is...").
5. If it says COUNT, every value that follows IS a count of records —
   present it as a count, never as a sum or average of anything.
6. If it says MIN, MAX, or MEDIAN, present the values as exactly that
   statistic — never relabel a median as an average, or a max as a total.
7. NEVER ask the user for record counts, totals, or any other number in
   order to "calculate" an average, rate, or percentage yourself — if the
   engine already performed that calculation, the answer is already in the
   computed result. You have no need to compute anything.
8. NEVER recalculate, re-derive, or independently reinterpret any number in
   the computed result — only explain it in natural language.
9. NEVER confuse a grouped breakdown (multiple values, one per category)
   with a single SUM — each value in a breakdown is its own result for its
   own group, computed using whatever aggregation is stated, not summed
   together into one number unless the computed result explicitly gives you
   a total.
10. Directly answer the user's original question in natural language,
    using the exact aggregation that was performed — do not switch to a
    different statistic than the one the engine actually computed.
`;

export default function ChatWindow({
  session,
  setActiveSession,
  setSessions,
  onOpenSubscription,
  isDemo = false,
  demoQuestionUsed = false,
  onDemoQuestionUsed,
  onRestrictedAction,
}) {

  const { limitReached, remaining, consumeAnalysisCredit, capabilities } = useSubscription();

  const [showDrawer, setShowDrawer] = useState(true);
  const [messages, setMessages] = useState(
    session?.messages || []
  );
  const [conversationContext, setConversationContext] = useState(
    session?.conversationContext || emptyContext()
  );

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportStage, setReportStage] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const messagesEndRef = useRef(null);
  useEffect(() => {
    setMessages(session?.messages || []);
    setConversationContext(session?.conversationContext || emptyContext());
  }, [session]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, loading]);

  // -----------------------------------
  // SEND MESSAGE
  // -----------------------------------
  const sendMessageText = async (rawText) => {

    const question = rawText.trim();

    if (!question || loading) return;

    setLoading(true);

    const userMsg = {
      role: "user",
      content: question,
    };

    const updatedMessages = [
      ...messages,
      userMsg,
    ];

    setMessages(updatedMessages);

    setInput("");

    let toolResult = null;

    try {

      const schema = buildDatasetSchema(
        session.dataset
      );

      // The most recent chart shown IN THIS CONVERSATION (not a persistent
      // dashboard chart — charts belong to the conversation now) lets a
      // follow-up like "make it a line chart" work without repeating
      // the dimension/measure.
      const lastChartMsg = [...messages].reverse().find(m => m.chart);
      const currentChart = lastChartMsg
        ? { chartType: lastChartMsg.chart.type, data: lastChartMsg.chart.data }
        : null;

      // 1. Groq sees ONLY the schema (+ the most recent chart + conversation
      //    context, so it can handle follow-ups like "what about 2013?" or
      //    "compare her with Ashish") and plans which local tools to run.
      //    It never sees a single data row. For pure chit-chat it returns
      //    no steps.
      const plan =
        await planQuery(
          question,
          schema,
          currentChart,
          conversationContext
        );

      if (process.env.NODE_ENV === "development") {
        console.log("[InsightPulse] plan:", plan);
      }

      const isDataQuestion = plan?.steps?.length > 0;

      // Demo visitors get exactly one successful AI-powered question. A
      // failed/errored request never reaches this point with success, so
      // it's never counted — matches the credit-consumption logic below,
      // which also only fires on toolResult.success.
      if (isDemo && isDataQuestion && demoQuestionUsed) {

        trackEvent("demo_limit_reached");

        const upgradeMsg = {
          role: "assistant",
          text: "You've explored InsightPulse with demo data. Create a free account to analyze your own data and continue asking questions.",
          limitReached: true,
          isDemoLimit: true,
        };

        const finalMessages = [...updatedMessages, upgradeMsg];

        setMessages(finalMessages);
        setActiveSession({ ...session, messages: finalMessages });

        setLoading(false);
        return;
      }

      // Only actual analytical requests count toward the plan's usage
      // limit — chit-chat, greetings, and unrelated questions never reach
      // here with isDataQuestion true, so they're free regardless of plan.
      if (!isDemo && isDataQuestion && limitReached) {

        const upgradeMsg = {
          role: "assistant",
          text: "You've used all of your included analyses for this month. Upgrade to Premium for significantly higher analysis limits and professional export features.",
          limitReached: true,
        };

        const finalMessages = [...updatedMessages, upgradeMsg];

        setMessages(finalMessages);

        const updatedSession = { ...session, messages: finalMessages };
        setActiveSession(updatedSession);
        setSessions((prev) => prev.map((s) => (s.firebaseId === session.firebaseId ? updatedSession : s)));

        try {
          await saveMessages(session.firebaseId, finalMessages);
        } catch (persistErr) {
          console.error("Failed to persist session:", persistErr);
        }

        setLoading(false);
        return;
      }

      let computedResult = null;

      if (isDataQuestion) {

        // 2. The local analytics engine runs the plan against the FULL
        //    dataset, entirely in-browser. The raw dataset never leaves here.
        toolResult = executePlan(plan, session.dataset);

        if (process.env.NODE_ENV === "development") {
          console.log("[InsightPulse] toolResult:", toolResult);
        }

        computedResult = formatToolResult(toolResult);

        if (process.env.NODE_ENV === "development") {
          console.log("[InsightPulse] computedResult sent to Groq:", computedResult);
        }

        // A credit/demo-question is consumed only for a valid, completed
        // analytical operation — not for a plan that failed to compute
        // anything.
        if (toolResult?.success) {
          if (isDemo) {
            onDemoQuestionUsed?.();
            trackEvent("demo_question_asked");
          } else {
            try {
              await consumeAnalysisCredit();
            } catch (creditErr) {
              console.error("Failed to record analysis usage:", creditErr);
            }
          }
        }
      }

      // 3. Build the system prompt. Data questions get the "explain this
      //    already-computed result" framing; everything else (greetings,
      //    thanks, small talk, meta questions) gets a plain conversational
      //    persona so Groq doesn't go looking for a result that isn't there.
      const systemPrompt = isDataQuestion
        ? `
${PERSONA_RULES}
${ANSWER_FORMAT_RULES}

For THIS message, you already have a computed result below — it reflects
any filtering described in it (e.g. a specific year or category). The record
count shown is the FILTERED count, not the whole dataset — do NOT say a
number is "overall" or that you lack year/category-specific data if a filter
is already noted below.

- Never recalculate, estimate, or say "I'll calculate" — just explain the
  result naturally and concisely.
- If the result is a chart, briefly describe what it now shows — it has
  already been drawn inline in this conversation.
- If the result contains an error, explain clearly that the question
  couldn't be answered with the available data, without inventing numbers.

Computed result:

${computedResult || "None"}

${buildAnswerContext(schema)}
`
        : `
${PERSONA_RULES}

This message is casual conversation, not a data question (e.g. a greeting,
thanks, or small talk) — there is no computed result for it. Just respond
naturally and warmly as InsightPulse AI. Do NOT say a result wasn't provided
or that you couldn't find data — there was never a calculation to do here.
`;

      const aiReply = await askGroq({
        messages: [
          { role: "system", content: systemPrompt },
          ...updatedMessages.map((m) => ({
            role: m.role,
            content: m.text || m.content,
          })),
        ],
      });

      // 4. Update conversational memory (measure/dimension/filters/time/
      //    entities/intent) so the NEXT message can be a natural follow-up
      //    without repeating everything.
      const newContext = updateContext(conversationContext, plan, toolResult);
      setConversationContext(newContext);

      // 5. Suggest natural next questions from the actual result + context
      //    (not generic hardcoded ones) for data questions that succeeded.
      let followUps = [];

      if (isDataQuestion && toolResult?.success) {
        followUps = await suggestFollowUpQuestions(
          question,
          computedResult,
          describeContext(newContext),
          schema
        );
      }

      const chartTitle =
        toolResult?.appliedGroupBy ? `${toolResult.appliedGroupBy.measure} by ${toolResult.appliedGroupBy.dimension}`
        : toolResult?.tool === "trend" ? `${toolResult.measure} over time`
        : toolResult?.tool === "compare" ? `${toolResult.measure}: ${toolResult.a?.label} vs ${toolResult.b?.label}`
        : toolResult?.tool === "driver" ? `What changed in ${toolResult.measure}`
        : toolResult?.dimension ? `${toolResult.measure} by ${toolResult.dimension}`
        : toolResult?.dateColumn ? `${toolResult.measure} over time`
        : null;

      const botMsg = {
        role: "assistant",
        text: aiReply,
        chart: toolResult?.outputMode?.includes("chart") && toolResult?.chartData?.length > 0
          ? { type: toolResult.suggestedChartType, data: toolResult.chartData, title: chartTitle }
          : null,
        table: toolResult?.outputMode?.includes("table") && toolResult?.tableData?.rows?.length > 0
          ? toolResult.tableData
          : null,
        followUps,
      };

      const finalMessages = [
        ...updatedMessages,
        botMsg,
      ];

      setMessages(finalMessages);

      const updatedSession = {
        ...session,
        messages: finalMessages,
        conversationContext: newContext,
      };

      setActiveSession(updatedSession);

      setSessions((prev) =>
        prev.map((s) =>
          s.firebaseId === session.firebaseId
            ? updatedSession
            : s
        )
      );

      // Persistence failures shouldn't surface as a scary chat error when
      // the reply/chart already rendered successfully — log and move on.
      // Demo sessions are intentionally never written to Firestore.
      if (!isDemo) {
        try {

          await saveMessages(
            session.firebaseId,
            finalMessages
          );

          await updateSession(session.firebaseId, {
            conversationContext: newContext,
          });

        } catch (persistErr) {
          console.error("Failed to persist session:", persistErr);
        }
      }

    } catch (err) {

      console.error(err);

      const errorMsg = {
        role: "assistant",
        text: "Sorry, I couldn't process that. Please try again.",
      };

      setMessages((prev) => [
        ...prev,
        errorMsg,
      ]);

    } finally {

      setLoading(false);

    }
  };

  // Auto-dismiss the export error toast so it doesn't linger forever.
  useEffect(() => {
    if (!exportError) return;
    const timer = setTimeout(() => setExportError(null), 6000);
    return () => clearTimeout(timer);
  }, [exportError]);

  const sendMessage = () => sendMessageText(input);

  // -----------------------------------
  // EXPORT REPORT / CONVERSATION
  // -----------------------------------
  const handleScheduleReportClick = () => {
    setShowExportMenu(false);

    if (isDemo) {
      onRestrictedAction?.("report");
      return;
    }

    if (!capabilities.canScheduleReports) {
      onOpenSubscription?.();
      return;
    }

    setShowScheduleModal(true);
  };

  const handleExport = async (kind) => {

    if (reportStage) return;

    if (isDemo) {
      setShowExportMenu(false);
      onRestrictedAction?.("export");
      return;
    }

    setShowExportMenu(false);

    const exportOptions = { watermark: capabilities.hasWatermark };

    setExportError(null);

    try {

      if (kind === "sales") {
        await generateSalesAnalysisReport(session, (stage) => setReportStage(stage), exportOptions);
      } else if (kind === "executive") {
        await generateExecutiveSummary(session, (stage) => setReportStage(stage), exportOptions);
      } else if (kind === "conversation") {
        setReportStage("Exporting conversation...");
        await exportConversation({ ...session, messages }, exportOptions);
        setReportStage(null);
      }

    } catch (err) {

      console.error("Export failed:", err);
      setExportError("Sorry, the export couldn't be generated. Please try again.");
      setReportStage(null);

    }
  };

  // -----------------------------------
  // ENTER KEY SUPPORT
  // -----------------------------------
  const handleKeyDown = (e) => {

    if (e.key === "Enter") {
      sendMessage();
    }
  };

  const showStarterQuestions = messages.length <= 1 && session?.suggestedQuestions?.length > 0;

  return (

    <div className="flex h-screen bg-gray-950 text-white overflow-hidden relative">

      {/* ===================================== */}
      {/* CHAT AREA */}
      {/* ===================================== */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* TOP BAR */}
        {/* relative + z-30: backdrop-blur below creates its own stacking
            context, which would otherwise trap the z-50 export dropdown
            inside it — letting later-painted chat content (charts) cover
            it when scrolled. Elevating the whole header fixes that. */}
        <div className="relative z-30 flex justify-between items-center px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-800/80 bg-gray-900/60 backdrop-blur-xl">

          <div className="flex items-center gap-2 sm:gap-3 min-w-0">

            <div className="w-8 h-8 sm:w-9 sm:h-9 shrink-0 flex items-center justify-center">
              <Logo size={32} className="w-full h-full" />
            </div>

            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold leading-tight truncate">
                InsightPulse AI
              </h1>

              <p className="text-gray-500 text-[11px] sm:text-xs hidden sm:block">
                Conversational Data Analytics
              </p>
            </div>

          </div>

          <div className="flex items-center gap-2 shrink-0">

            {capabilities.monthlyAnalysisLimit < 100 && remaining <= 2 && (
              <button
                onClick={onOpenSubscription}
                title="View subscription"
                className={`hidden sm:block text-xs px-3 py-2 rounded-xl font-medium border transition ${
                  remaining === 0
                    ? "bg-red-950/40 border-red-800/60 text-red-300 hover:bg-red-950/60"
                    : "bg-amber-950/30 border-amber-800/50 text-amber-300 hover:bg-amber-950/50"
                }`}
              >
                {remaining} analys{remaining === 1 ? "is" : "es"} left
              </button>
            )}

            <div className="relative">

              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={!!reportStage}
                className="text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition px-3 sm:px-4 py-2 rounded-xl font-medium flex items-center gap-2"
              >
                {reportStage ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    <span className="hidden sm:inline">{reportStage}</span>
                  </>
                ) : (
                  <>📄 <span className="hidden sm:inline">Export</span></>
                )}
              </button>

              {showExportMenu && !reportStage && (

                <>
                  <div
                    onClick={() => setShowExportMenu(false)}
                    className="fixed inset-0 z-40"
                  />

                  <div className="absolute right-0 mt-2 w-64 max-w-[85vw] bg-gray-900 border border-gray-800 rounded-xl shadow-2xl z-50 overflow-hidden">

                    <button
                      onClick={() => handleExport("sales")}
                      className="w-full text-left px-4 py-3 hover:bg-gray-800 transition border-b border-gray-800"
                    >
                      <div className="font-medium text-sm">Sales Analysis Report</div>
                      <div className="text-xs text-gray-500 mt-0.5">Detailed, multi-page — for analysts and managers</div>
                    </button>

                    <button
                      onClick={() => handleExport("executive")}
                      className="w-full text-left px-4 py-3 hover:bg-gray-800 transition border-b border-gray-800"
                    >
                      <div className="font-medium text-sm">Executive Summary</div>
                      <div className="text-xs text-gray-500 mt-0.5">Concise, 2 pages — for leadership</div>
                    </button>

                    <button
                      onClick={() => handleExport("conversation")}
                      className="w-full text-left px-4 py-3 hover:bg-gray-800 transition border-b border-gray-800"
                    >
                      <div className="font-medium text-sm">Export Conversation</div>
                      <div className="text-xs text-gray-500 mt-0.5">This chat, with charts and tables, as a PDF</div>
                    </button>

                    <button
                      onClick={handleScheduleReportClick}
                      className="w-full text-left px-4 py-3 hover:bg-gray-800 transition"
                    >
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        📬 Schedule Report
                        {!isDemo && !capabilities.canScheduleReports && (
                          <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full font-semibold text-gray-300">PRO</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">Schedule this report to be sent once, by email</div>
                    </button>

                  </div>
                </>

              )}

            </div>

            <button
              onClick={() =>
                setShowDrawer(!showDrawer)
              }
              className="text-xs sm:text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 transition px-3 sm:px-4 py-2 rounded-xl font-medium"
            >
              {showDrawer
                ? "Hide Insights"
                : "Show Insights"}
            </button>

          </div>

        </div>

        {/* MESSAGES */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4">

          {messages.map((msg, i) => (

            <div key={i}>

              <div
                className={`flex items-end gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >

                {msg.role !== "user" && (
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs shrink-0 mb-1">
                    ✦
                  </div>
                )}

                <div
                  className={`max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl shadow-sm text-[14px] sm:text-[15px] leading-relaxed
                  ${msg.role === "user"
                      ? "bg-blue-600 rounded-br-md"
                      : msg.limitReached
                      ? "bg-amber-950/40 border border-amber-800/60 rounded-bl-md"
                      : "bg-gray-900 border border-gray-800 rounded-bl-md"
                    }`}
                >
                  <p className="whitespace-pre-wrap">
                    {msg.text || msg.content}
                  </p>

                  {msg.limitReached && (
                    <button
                      onClick={onOpenSubscription}
                      className="mt-3 bg-blue-600 hover:bg-blue-700 transition px-4 py-2 rounded-lg text-sm font-semibold"
                    >
                      {msg.isDemoLimit ? "Create Free Account" : "Upgrade to Premium"}
                    </button>
                  )}
                </div>

              </div>

              {/* Inline auto-visualization: same verified result as the text answer */}
              {msg.chart?.data?.length > 0 && (
                <div className="ml-9 mt-2 max-w-[90%] sm:max-w-[75%] bg-gray-900/60 border border-gray-800 rounded-2xl p-3">
                  <ExportableChart
                    data={msg.chart.data}
                    type={msg.chart.type === "pie" ? "pie" : msg.chart.type === "line" ? "line" : "bar"}
                    title={msg.chart.title}
                    subtitle={session?.name}
                    watermark={capabilities.hasWatermark}
                    locked={isDemo}
                    onLockedAction={() => onRestrictedAction?.("export-chart")}
                  />
                </div>
              )}

              {/* Supporting data table */}
              {msg.table?.rows?.length > 0 && (
                <div className="ml-9 mt-2 max-w-[90%] sm:max-w-[75%] bg-gray-900/60 border border-gray-800 rounded-xl overflow-hidden text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-800/60 text-gray-400">
                        {msg.table.headers.map((h, hi) => (
                          <th key={hi} className="text-left px-3 py-2 font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {msg.table.rows.map((row, ri) => (
                        <tr key={ri} className="border-t border-gray-800">
                          {row.cells.map((cell, ci) => (
                            <td key={ci} className="px-3 py-1.5 text-gray-300">
                              {typeof cell === "number" ? cell.toLocaleString(undefined, { maximumFractionDigits: 2 }) : cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Suggested follow-up questions */}
              {msg.followUps?.length > 0 && (
                <div className="ml-9 mt-2 flex flex-wrap gap-2 max-w-[90%] sm:max-w-[75%]">
                  {msg.followUps.map((q, qi) => (
                    <button
                      key={qi}
                      onClick={() => sendMessageText(q)}
                      disabled={loading}
                      className="text-xs bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-blue-500/60 transition px-3 py-1.5 rounded-full text-gray-300 disabled:opacity-50"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

            </div>

          ))}

          {/* Starter suggested questions, shown right after upload */}
          {showStarterQuestions && (
            <div className="ml-9 flex flex-wrap gap-2 max-w-[90%] sm:max-w-[85%]">
              {session.suggestedQuestions.map((q, qi) => (
                <button
                  key={qi}
                  onClick={() => sendMessageText(q)}
                  disabled={loading}
                  className="text-xs sm:text-sm bg-gray-900 hover:bg-gray-800 border border-gray-700 hover:border-blue-500/60 transition px-3 py-2 rounded-full text-gray-300 disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {loading && (

            <div className="flex items-end gap-2 justify-start">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs shrink-0 mb-1">
                ✦
              </div>

              <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-gray-900 border border-gray-800">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce" />
                </span>
              </div>
            </div>

          )}

          <div ref={messagesEndRef} />

        </div>

        {/* INPUT AREA */}
        <div className="p-2.5 sm:p-4 border-t border-gray-800/80 bg-gray-900/60 backdrop-blur-xl">

          <div className="flex gap-2 sm:gap-3 items-center bg-gray-900 border border-gray-800 rounded-2xl px-2 py-2 focus-within:border-blue-500/60 transition">

            <input
              disabled={loading}
              value={input}
              onChange={(e) =>
                setInput(e.target.value)
              }
              onKeyDown={handleKeyDown}
              placeholder="Ask about your data..."
              className="flex-1 min-w-0 bg-transparent px-2 sm:px-3 py-2 outline-none text-[14px] sm:text-[15px] placeholder:text-gray-600"
            />

            <button
              disabled={loading || !input.trim()}
              onClick={sendMessage}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition px-4 sm:px-5 py-2.5 rounded-xl font-semibold text-sm shrink-0"
            >
              {loading ? "…" : "Send"}
            </button>

          </div>

        </div>

      </div>

      {/* ===================================== */}
      {/* INSIGHTS DRAWER */}
      {/* ===================================== */}
      {showDrawer && (

        <>
          {/* backdrop on mobile so the drawer reads as an overlay, not a squeeze */}
          <div
            onClick={() => setShowDrawer(false)}
            className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          />

          <div className="fixed inset-y-0 right-0 z-40 w-[88%] max-w-[420px] lg:static lg:z-auto lg:w-[420px] shrink-0 border-l border-gray-800/80 bg-gray-950 lg:bg-gray-900/40 overflow-y-auto shadow-2xl lg:shadow-none">

            <AutoDashboard session={session} onAskQuestion={sendMessageText} />

          </div>
        </>

      )}

      {/* EXPORT ERROR TOAST */}
      {exportError && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-[60] sm:max-w-sm">
          <div className="bg-red-950/90 border border-red-800/60 text-red-200 rounded-xl px-4 py-3 shadow-2xl flex items-start gap-2.5 backdrop-blur-sm">
            <span className="text-red-400 shrink-0 mt-0.5">⚠️</span>
            <p className="text-sm leading-snug flex-1">{exportError}</p>
            <button
              onClick={() => setExportError(null)}
              aria-label="Dismiss"
              className="text-red-400 hover:text-red-200 transition shrink-0 text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {!isDemo && (
        <ScheduleReportModal
          open={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          session={session}
        />
      )}

    </div>
  );
}
